(() => {
  const tokenKey = "beanited:saved-learning-token";
  const escapeHtml = (value) => String(value ?? "").replace(/[&<>'"]/g, (character) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", "'": "&#39;", '"': "&quot;" })[character]);
  const enabled = (host, key, fallback = true) => host.dataset[key] == null ? fallback : host.dataset[key] === "true";
  const starMarkup = (rating) => `<span class="beanited-reviews__stars">${"★".repeat(Number(rating))}</span><span class="beanited-reviews__empty-stars">${"☆".repeat(5 - Number(rating))}</span>`;
  const formatDate = (value) => new Intl.DateTimeFormat(undefined, { day: "numeric", month: "short", year: "numeric" }).format(new Date(value));
  function reviewText(review, host) {
    const maximumCharacters = Number(host.dataset.maxCharacters || 0);
    const maximumLines = Number(host.dataset.maxLines || 0);
    const truncated = maximumCharacters > 0 && review.reviewText.length > maximumCharacters;
    const visible = truncated ? `${review.reviewText.slice(0, maximumCharacters).trim()}…` : review.reviewText;
    const collapse = maximumLines > 0 || truncated;
    return `<p class="beanited-review-card__text" ${collapse ? `data-collapsed="true" style="--br-max-lines:${maximumLines || 99}"` : ""} data-full-text="${escapeHtml(review.reviewText)}" data-preview-text="${escapeHtml(visible)}">${escapeHtml(visible)}</p>${collapse ? '<button type="button" class="beanited-review-card__read-more" data-beanited-read-more aria-expanded="false">Read more</button>' : ""}`;
  }
  function card(review, host, showProduct = false) {
    const reviewer = enabled(host, "showReviewer") ? `<strong class="beanited-review-card__name">${escapeHtml(review.reviewerName)}</strong>` : "";
    const reviewDate = enabled(host, "showDate") ? `<time class="beanited-review-card__date" datetime="${escapeHtml(review.reviewDate)}">${formatDate(review.reviewDate)}</time>` : "";
    const rating = enabled(host, "showStars") ? `<span aria-label="${review.rating} out of 5 stars">${starMarkup(review.rating)}</span>` : "";
    const numeric = enabled(host, "showNumeric", false) ? `<span class="beanited-review-card__numeric">${Number(review.rating).toFixed(1)}</span>` : "";
    const productName = showProduct && enabled(host, "showProduct") ? `<span class="beanited-review-card__product">${escapeHtml(review.productTitle)}</span>` : "";
    const roaster = showProduct && review.brand && enabled(host, "showRoaster") ? `<span class="beanited-review-card__roaster"> · ${escapeHtml(review.brand)}</span>` : "";
    const meta = productName || roaster ? `<p class="beanited-review-card__meta">${productName}${roaster}</p>` : "";
    const verified = review.verifiedPurchase && enabled(host, "showVerified") ? '<span class="beanited-review-card__verified">✓ Verified purchase</span>' : "";
    const photos = review.photos?.length && enabled(host, "showImages") ? `<div class="beanited-review-card__photos">${review.photos.slice(0, 2).map((url) => `<img src="${escapeHtml(url)}" alt="Customer review photo" loading="lazy">`).join("")}</div>` : "";
    const reply = review.reply && enabled(host, "showReply") ? `<div class="beanited-review-card__reply"><strong>Roaster reply</strong><p>${escapeHtml(review.reply.replyText)}</p></div>` : "";
    const productUrl = showProduct && review.productUrl ? escapeHtml(review.productUrl) : "";
    const link = productUrl && enabled(host, "showProductLink") ? `<a class="beanited-review-card__view" href="${productUrl}">View product</a>` : "";
    const clickable = productUrl ? ` data-beanited-product-url="${productUrl}" role="link" tabindex="0" aria-label="View ${escapeHtml(review.productTitle || "reviewed product")}"` : "";
    return `<article class="beanited-review-card"${clickable}><div class="beanited-review-card__top"><div>${reviewer}<p class="beanited-review-card__rating">${rating}${numeric}</p></div>${reviewDate}</div>${meta}${reviewText(review, host)}${verified}${photos}${reply}${link}</article>`;
  }
  function setupReadMore(root) {
    root.querySelectorAll("[data-beanited-read-more]").forEach((button) => {
      if (button.dataset.ready) return;
      button.dataset.ready = "true";
      button.addEventListener("click", () => {
        const text = button.previousElementSibling;
        const isCollapsed = text.dataset.collapsed !== "false";
        if (isCollapsed) {
          text.dataset.collapsed = "false";
          text.textContent = text.dataset.fullText;
          button.textContent = "Show less";
          button.setAttribute("aria-expanded", "true");
        } else {
          text.dataset.collapsed = "true";
          text.textContent = text.dataset.previewText;
          button.textContent = "Read more";
          button.setAttribute("aria-expanded", "false");
        }
      });
    });
  }
  function setupCardLinks(root) {
    root.querySelectorAll("[data-beanited-product-url]").forEach((card) => {
      if (card.dataset.linkReady) return;
      card.dataset.linkReady = "true";
      const openProduct = () => { window.location.href = card.dataset.beanitedProductUrl; };
      card.addEventListener("click", (event) => {
        if (event.target.closest("a,button,input,select,textarea,label")) return;
        openProduct();
      });
      card.addEventListener("keydown", (event) => {
        if (event.target !== card || (event.key !== "Enter" && event.key !== " ")) return;
        event.preventDefault();
        openProduct();
      });
    });
  }
  async function loadProduct(host) {
    if (host.dataset.ready) return;
    host.dataset.ready = "true";
    const origin = new URL(host.dataset.apiOrigin).origin;
    const list = host.querySelector("[data-beanited-review-list]");
    const more = host.querySelector("[data-beanited-load-more]");
    const filterEmpty = host.querySelector("[data-beanited-review-filter-empty]");
    const controls = host.querySelector("[data-beanited-review-controls]");
    const sortControl = host.querySelector("[data-beanited-sort]");
    const ratingControl = host.querySelector("[data-beanited-rating-filter]");
    const photoControl = host.querySelector("[data-beanited-photo-filter]");
    let offset = 0;
    const limit = Number(host.dataset.limit || 6);
    async function page(reset = false) {
      if (reset) { offset = 0; list.innerHTML = ""; }
      more.disabled = true;
      filterEmpty.hidden = true;
      const url = new URL("/api/shopify/proxy/product-reviews", origin);
      url.searchParams.set("product_id", host.dataset.productId);
      url.searchParams.set("limit", String(limit));
      url.searchParams.set("offset", String(offset));
      if (sortControl?.value) url.searchParams.set("sort", sortControl.value);
      if (ratingControl?.value) url.searchParams.set("rating", ratingControl.value);
      if (photoControl?.checked) url.searchParams.set("with_photos", "true");
      const response = await fetch(url, { headers: { Accept: "application/json" } });
      const body = await response.json();
      if (!response.ok) throw new Error(body.error || "Reviews unavailable");
      host.querySelector("[data-beanited-review-loading]").hidden = true;
      if (!body.rating.count) { host.querySelector("[data-beanited-review-empty]").hidden = false; return; }
      host.querySelector("[data-beanited-review-empty]").hidden = true;
      host.querySelector("[data-beanited-review-content]").hidden = false;
      const average = host.querySelector("[data-beanited-average]");
      if (average) average.textContent = body.rating.average.toFixed(1);
      host.querySelector("[data-beanited-summary-stars]")?.setAttribute("aria-label", `${body.rating.average} out of 5 stars`);
      const total = host.querySelector("[data-beanited-total]");
      if (total) total.textContent = `Based on ${body.rating.count} ${body.rating.count === 1 ? "review" : "reviews"}`;
      const distribution = host.querySelector("[data-beanited-distribution]");
      if (distribution) distribution.innerHTML = [5, 4, 3, 2, 1].map((value) => { const count = body.rating.distribution[value] || 0; const percent = body.rating.count ? Math.round(count / body.rating.count * 100) : 0; return `<div><span>${value} star</span><span class="beanited-reviews__bar"><i style="width:${percent}%"></i></span><span>${count}</span></div>`; }).join("");
      if (controls) {
        controls.hidden = !(enabled(host, "sortEnabled") || enabled(host, "starFilterEnabled") || enabled(host, "photoFilterEnabled"));
        host.querySelector("[data-beanited-sort-wrap]").hidden = !enabled(host, "sortEnabled");
        host.querySelector("[data-beanited-rating-wrap]").hidden = !enabled(host, "starFilterEnabled");
        host.querySelector("[data-beanited-photo-wrap]").hidden = !enabled(host, "photoFilterEnabled");
      }
      list.insertAdjacentHTML("beforeend", body.reviews.map((review) => card(review, host)).join(""));
      setupReadMore(list);
      setupCardLinks(list);
      if (reset && !body.reviews.length) filterEmpty.hidden = false;
      offset += body.reviews.length;
      more.hidden = !body.pagination.hasMore;
      more.disabled = false;
    }
    more.addEventListener("click", () => page().catch(() => { more.disabled = false; }));
    [sortControl, ratingControl, photoControl].filter(Boolean).forEach((control) => control.addEventListener("change", () => page(true).catch(() => { host.querySelector("[data-beanited-review-error]").hidden = false; })));
    try { await page(); } catch { host.querySelector("[data-beanited-review-loading]").hidden = true; host.querySelector("[data-beanited-review-error]").hidden = false; }
    setupSubmission(host, origin);
  }
  function savedToken() { try { const value = JSON.parse(sessionStorage.getItem(tokenKey) || "null"); return value && Date.now() < value.expiresAt ? value.accessToken : null; } catch { return null; } }
  function authenticate(origin) { return new Promise((resolve, reject) => { const url = new URL("/api/shopify/customer-account/start", origin); url.searchParams.set("origin", location.origin); const popup = open(url, "beanited-review-auth", "popup,width=520,height=720"); if (!popup) return reject(new Error("Please allow the sign-in popup.")); const timer = setTimeout(() => done(new Error("Sign in timed out.")), 120000); function receive(event) { if (event.origin !== origin || event.source !== popup || event.data?.type !== "beanited:saved-learning-auth") return; if (!event.data.ok) return done(new Error("Sign in failed.")); sessionStorage.setItem(tokenKey, JSON.stringify({ accessToken: event.data.accessToken, expiresAt: Date.now() + Math.max(0, Number(event.data.expiresIn) - 60) * 1000 })); done(null, event.data.accessToken); } function done(error, value) { clearTimeout(timer); removeEventListener("message", receive); error ? reject(error) : resolve(value); } addEventListener("message", receive); }); }
  function setupSubmission(host, origin) {
    const button = host.querySelector("[data-beanited-write-review]"); const panel = host.querySelector("[data-beanited-review-form-panel]"); const form = host.querySelector("[data-beanited-submit-form]"); const status = host.querySelector("[data-beanited-submit-status]"); let context = "";
    button.addEventListener("click", async () => { button.disabled = true; status.textContent = ""; try { const token = savedToken() || await authenticate(origin); const url = new URL("/api/shopify/proxy/reviews", origin); url.searchParams.set("product_id", host.dataset.productId); const response = await fetch(url, { headers: { Accept: "application/json", Authorization: `Bearer ${token}` } }); const body = await response.json(); if (!response.ok) throw new Error(body.error || "Review form unavailable."); context = body.context; panel.hidden = false; panel.scrollIntoView({ behavior: matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth", block: "start" }); } catch (error) { status.textContent = error.message; panel.hidden = false; } finally { button.disabled = false; } });
    form.addEventListener("submit", async (event) => { event.preventDefault(); if (!form.reportValidity()) return; const files = Array.from(form.elements.photos.files || []); if (files.length > 2) { status.textContent = "Choose no more than 2 photos."; return; } const submit = form.querySelector("[type=submit]"); submit.disabled = true; status.textContent = "Uploading and submitting your review…"; try { const token = savedToken() || await authenticate(origin); const data = new FormData(form); data.set("context", context); const response = await fetch(new URL("/api/shopify/proxy/reviews", origin), { method: "POST", headers: { Accept: "application/json", Authorization: `Bearer ${token}` }, body: data }); const body = await response.json(); if (!response.ok) throw new Error(body.error === "duplicate_review" ? "You have already reviewed this order and coffee." : body.error || "Review submission failed."); form.reset(); status.textContent = "Thanks. Your review will appear after moderation."; submit.hidden = true; } catch (error) { status.textContent = error.message; } finally { submit.disabled = false; } });
  }
  let ratingTimer;
  function loadRatings() { clearTimeout(ratingTimer); ratingTimer = setTimeout(async () => { const nodes = [...document.querySelectorAll("[data-beanited-rating]:not([data-ready])")]; if (!nodes.length) return; nodes.forEach((node) => { node.dataset.ready = "true"; }); const groups = Map.groupBy ? Map.groupBy(nodes, (node) => node.dataset.apiOrigin) : nodes.reduce((map, node) => map.set(node.dataset.apiOrigin, [...(map.get(node.dataset.apiOrigin) || []), node]), new Map()); for (const [originValue, group] of groups) { try { const url = new URL("/api/shopify/proxy/product-review-aggregates", new URL(originValue).origin); url.searchParams.set("product_ids", [...new Set(group.map((node) => node.dataset.productId))].slice(0, 50).join(",")); const response = await fetch(url); const body = await response.json(); if (!response.ok) continue; group.forEach((node) => { const rating = body.ratings[node.dataset.productId]; if (!rating?.count) return; node.querySelector("[data-beanited-rating-text]").textContent = `${rating.average.toFixed(1)} (${rating.count})`; node.hidden = false; }); } catch {} } }, 0); }
  function setupCarousel(host, list) {
    if (host.dataset.layout !== "carousel") return;
    const viewport = host.querySelector("[data-beanited-carousel-viewport]");
    const navigation = host.querySelector("[data-beanited-carousel-navigation]");
    const previous = host.querySelector("[data-beanited-previous]");
    const next = host.querySelector("[data-beanited-next]");
    const dots = host.querySelector("[data-beanited-carousel-dots]");
    if (!viewport || !navigation || !previous || !next || !dots) return;

    const reducedMotion = () => matchMedia("(prefers-reduced-motion: reduce)").matches;
    let pageTargets = [0];
    let indicatorPages = [0];
    let dotButtons = [];
    let resizeFrame = 0;

    const maxScroll = () => Math.max(0, viewport.scrollWidth - viewport.clientWidth);
    const cardPositions = () => {
      const maximum = maxScroll();
      return [...list.children].map((item) => Math.min(maximum, Math.max(0, item.offsetLeft - list.offsetLeft)));
    };
    const nearestIndex = (targets, value) => targets.reduce((best, target, index) => Math.abs(target - value) < Math.abs(targets[best] - value) ? index : best, 0);

    const measurePages = () => {
      const cards = [...list.children];
      const maximum = maxScroll();
      if (!cards.length || maximum <= 2) {
        pageTargets = [0];
        indicatorPages = [0];
        return;
      }

      const cardWidth = cards[0].getBoundingClientRect().width;
      const styles = getComputedStyle(list);
      const gap = parseFloat(styles.columnGap || styles.gap || "0") || 0;
      const cardsPerPage = Math.max(1, Math.floor((viewport.clientWidth + gap + 1) / (cardWidth + gap)));
      const positions = cardPositions();
      const targets = [];

      for (let index = 0; index < cards.length; index += cardsPerPage) {
        targets.push(positions[index]);
      }
      targets.push(maximum);
      pageTargets = targets.filter((target, index, values) => index === 0 || Math.abs(target - values[index - 1]) > 2);

      if (pageTargets.length <= 3) {
        indicatorPages = pageTargets.map((_, index) => index);
      } else {
        indicatorPages = [0, Math.floor((pageTargets.length - 1) / 2), pageTargets.length - 1];
      }
    };

    const activePage = () => nearestIndex(pageTargets, viewport.scrollLeft);
    const activeIndicator = () => {
      const page = activePage();
      if (indicatorPages.length <= 1) return 0;
      if (page <= indicatorPages[0]) return 0;
      if (page >= indicatorPages[indicatorPages.length - 1]) return indicatorPages.length - 1;
      if (indicatorPages.length === 3) return 1;
      return nearestIndex(indicatorPages, page);
    };

    const updateNavigation = () => {
      const page = activePage();
      const indicator = activeIndicator();
      dotButtons.forEach((dot, index) => {
        const active = index === indicator;
        if (active) dot.setAttribute("aria-current", "true");
        else dot.removeAttribute("aria-current");
        dot.style.background = active ? "var(--br-nav,#1f1f1f)" : "";
      });
      previous.disabled = page <= 0;
      next.disabled = page >= pageTargets.length - 1;
    };

    const scrollToPage = (pageIndex) => {
      const safeIndex = Math.max(0, Math.min(pageTargets.length - 1, pageIndex));
      viewport.scrollTo({ left: pageTargets[safeIndex], behavior: reducedMotion() ? "auto" : "smooth" });
    };

    const renderDots = () => {
      dots.innerHTML = "";
      const labels = indicatorPages.length === 3 ? ["Beginning", "Middle", "End"] : indicatorPages.map((_, index) => index === 0 ? "Beginning" : "End");
      dotButtons = indicatorPages.map((pageIndex, index) => {
        const dot = document.createElement("button");
        dot.type = "button";
        dot.setAttribute("aria-label", `Go to ${labels[index].toLowerCase()} of reviews`);
        dot.addEventListener("click", () => scrollToPage(pageIndex));
        dots.appendChild(dot);
        return dot;
      });
      dots.hidden = !enabled(host, "dots") || dotButtons.length <= 1;
    };

    const recalculate = () => {
      const progress = maxScroll() > 0 ? viewport.scrollLeft / maxScroll() : 0;
      measurePages();
      renderDots();
      if (pageTargets.length > 1 && progress > 0) {
        const desired = progress * maxScroll();
        viewport.scrollLeft = pageTargets[nearestIndex(pageTargets, desired)];
      }
      updateNavigation();
      navigation.hidden = (!enabled(host, "arrows") || pageTargets.length <= 1) && (!enabled(host, "dots") || indicatorPages.length <= 1);
    };

    previous.hidden = !enabled(host, "arrows");
    next.hidden = !enabled(host, "arrows");
    previous.addEventListener("click", () => scrollToPage(activePage() - 1));
    next.addEventListener("click", () => scrollToPage(activePage() + 1));
    viewport.addEventListener("scroll", () => requestAnimationFrame(updateNavigation), { passive: true });

    const observer = typeof ResizeObserver === "function" ? new ResizeObserver(() => {
      cancelAnimationFrame(resizeFrame);
      resizeFrame = requestAnimationFrame(recalculate);
    }) : null;
    observer?.observe(viewport);
    observer?.observe(list);
    if (!observer) addEventListener("resize", () => {
      cancelAnimationFrame(resizeFrame);
      resizeFrame = requestAnimationFrame(recalculate);
    });

    requestAnimationFrame(recalculate);

    if (enabled(host, "autoplay") && !reducedMotion()) setInterval(() => {
      const page = activePage();
      scrollToPage(page >= pageTargets.length - 1 ? 0 : page + 1);
    }, Number(host.dataset.autoplaySpeed || 6) * 1000);
  }
  async function loadFeatured(host) { if (host.dataset.ready) return; host.dataset.ready = "true"; try { const url = new URL("/api/shopify/proxy/featured-reviews", new URL(host.dataset.apiOrigin).origin); url.searchParams.set("limit", host.dataset.limit || "8"); url.searchParams.set("selection", host.dataset.selection || "newest"); const response = await fetch(url); const body = await response.json(); if (!response.ok) throw new Error(); host.querySelector("[data-beanited-featured-loading]").hidden = true; if (!body.reviews.length) { host.querySelector("[data-beanited-featured-empty]").hidden = false; return; } const viewport = host.querySelector("[data-beanited-carousel-viewport]"); const list = host.querySelector("[data-beanited-featured-list]"); list.innerHTML = body.reviews.map((review) => card(review, host, true)).join(""); viewport.hidden = false; setupReadMore(list); setupCardLinks(list); setupCarousel(host, list); } catch { host.querySelector("[data-beanited-featured-loading]").textContent = "Reviews are temporarily unavailable."; } }
  function init(root = document) { root.querySelectorAll("[data-beanited-product-reviews]").forEach(loadProduct); root.querySelectorAll("[data-beanited-featured-reviews]").forEach(loadFeatured); loadRatings(); }
  document.readyState === "loading" ? document.addEventListener("DOMContentLoaded", () => init()) : init();
  document.addEventListener("shopify:section:load", (event) => init(event.target));
})();